import React, { useState, useEffect } from 'react';
import { get, put } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const ROLE_LABELS = {
  super_admin:'Managing Director', admin:'Administrator',
  sales_manager:'Sales Manager', content_sales:'Content & Sales',
  inventory_officer:'Inventory Officer', production_supervisor:'Production Supervisor',
  production_assistant:'Production Assistant', production_staff:'Production Staff',
  tailor:'Tailor', driver:'Driver', auditor:'Auditor'
};

const MONTHS = ['January','February','March','April','May','June',
  'July','August','September','October','November','December'];

export default function BirthdayCalendar() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [upcoming, setUpcoming] = useState([]);
  const [calendar, setCalendar] = useState([]);
  const [bonusSettings, setBonusSettings] = useState([]);
  const [bonusRequests, setBonusRequests] = useState([]);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [loading, setLoading] = useState(true);
  const [savingBonus, setSavingBonus] = useState(false);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const load = () => {
    setLoading(true);
    Promise.all([
      get('/birthday/upcoming'),
      get('/birthday/calendar'),
      ...(isSuperAdmin ? [get('/birthday/bonus-settings'), get('/birthday/bonus-requests')] : [])
    ]).then(([up, cal, bs, br]) => {
      setUpcoming(up.data||[]);
      setCalendar(cal.data||[]);
      if (bs) setBonusSettings(bs.data||[]);
      if (br) setBonusRequests(br.data||[]);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const saveBonus = async () => {
    setSavingBonus(true);
    try {
      await put('/birthday/bonus-settings', { settings: bonusSettings });
      alert('Bonus settings saved');
    } catch(err) { alert(err.message); } finally { setSavingBonus(false); }
  };

  const approveBonus = async (id) => {
    if (!window.confirm('Approve this birthday bonus?')) return;
    try {
      await put('/birthday/bonus-requests/'+id+'/approve', {});
      load();
    } catch(err) { alert(err.message); }
  };

  const rejectBonus = async () => {
    try {
      await put('/birthday/bonus-requests/'+rejectModal+'/reject', { reason: rejectReason });
      setRejectModal(null); setRejectReason('');
      load();
    } catch(err) { alert(err.message); }
  };

  // Group calendar by month
  const byMonth = MONTHS.map((month, idx) => ({
    month,
    staff: calendar.filter(s => parseInt(s.birth_month) === idx+1)
  })).filter(m => m.staff.length > 0);

  const todayBirthdays = upcoming.filter(s => parseInt(s.days_until) === 0);

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">🎂 Birthday Calendar</h2>
          <p className="text-muted text-sm" style={{marginTop:4}}>
            Staff birthdays, greetings and bonus management
          </p>
        </div>
      </div>

      {/* Today's birthdays banner */}
      {todayBirthdays.length > 0 && (
        <div style={{background:'linear-gradient(135deg,#556B2F,#A8893D)',
          borderRadius:10,padding:'16px 20px',marginBottom:20,
          display:'flex',alignItems:'center',gap:16}}>
          <span style={{fontSize:40}}>🎂</span>
          <div>
            <div style={{fontSize:16,fontWeight:700,color:'#F2EFE7',marginBottom:4}}>
              {todayBirthdays.map(s=>s.name).join(' & ')} {todayBirthdays.length===1?'is':'are'} celebrating a birthday today!
            </div>
            <div style={{fontSize:13,color:'rgba(242,239,231,.7)'}}>
              Wish them a happy birthday! 🎉
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{display:'flex',gap:6,marginBottom:20,borderBottom:'1px solid var(--border-light)',paddingBottom:12}}>
        {[
          ['upcoming','Upcoming Birthdays'],
          ['calendar','Birthday Calendar'],
          ...(isSuperAdmin ? [['bonus','Bonus Settings'],['requests','Bonus Requests']] : [])
        ].map(([key,label])=>(
          <button key={key} className={'btn btn-sm '+(activeTab===key?'btn-primary':'btn-ghost')}
            onClick={()=>setActiveTab(key)}>{label}</button>
        ))}
      </div>

      {loading ? <div className="spinner"></div> : (
        <>
          {/* Upcoming birthdays */}
          {activeTab==='upcoming' && (
            <div>
              {upcoming.length === 0 ? (
                <div className="empty-state">
                  <h3>No birthdays on record</h3>
                  <p>Add date of birth to staff profiles to see upcoming birthdays.</p>
                </div>
              ) : (
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:12}}>
                  {upcoming.map(s => {
                    const isToday = parseInt(s.days_until) === 0;
                    const isSoon = parseInt(s.days_until) <= 7;
                    return (
                      <div key={s.id} style={{background:'#fff',borderRadius:10,overflow:'hidden',
                        border:'2px solid '+(isToday?'#A8893D':isSoon?'#556B2F':'var(--border-light)'),
                        boxShadow:isToday?'0 4px 20px rgba(168,137,61,.3)':'var(--shadow)'}}>
                        {isToday && (
                          <div style={{background:'linear-gradient(135deg,#A8893D,#556B2F)',
                            padding:'6px 12px',textAlign:'center',fontSize:11,
                            fontWeight:700,color:'#F2EFE7',letterSpacing:'.05em'}}>
                            🎂 TODAY!
                          </div>
                        )}
                        <div style={{padding:'14px 16px',display:'flex',gap:12,alignItems:'center'}}>
                          <div style={{width:48,height:48,borderRadius:'50%',flexShrink:0,
                            background:'#556B2F',display:'flex',alignItems:'center',
                            justifyContent:'center',overflow:'hidden',
                            border:'2px solid '+(isToday?'#A8893D':'transparent')}}>
                            {s.photo_url
                              ? <img src={s.photo_url} alt={s.name} style={{width:'100%',height:'100%',objectFit:'cover'}} />
                              : <span style={{fontSize:20,fontWeight:700,color:'#F2EFE7'}}>{s.name?.charAt(0)}</span>
                            }
                          </div>
                          <div>
                            <div style={{fontSize:13,fontWeight:600,color:'#3B2A21'}}>{s.name}</div>
                            <div style={{fontSize:11,color:'#8A8A8A'}}>{ROLE_LABELS[s.role]||s.role}</div>
                            <div style={{fontSize:11,color:isToday?'#A8893D':isSoon?'#556B2F':'#8A8A8A',
                              fontWeight:isToday||isSoon?600:400,marginTop:2}}>
                              🎂 {s.birthday_display}
                              {isToday ? ' — Today!' : ` — in ${s.days_until} day${s.days_until!=='1'?'s':''}`}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Calendar view by month */}
          {activeTab==='calendar' && (
            <div>
              {byMonth.length === 0 ? (
                <div className="empty-state">
                  <h3>No birthdays on record</h3>
                  <p>Add date of birth to staff profiles.</p>
                </div>
              ) : (
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:16}}>
                  {byMonth.map(({ month, staff }) => (
                    <div key={month} style={{background:'#fff',border:'1px solid var(--border-light)',
                      borderRadius:10,overflow:'hidden'}}>
                      <div style={{background:'#3B2A21',padding:'10px 16px',
                        display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                        <span style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1rem',
                          color:'#A8893D',fontWeight:600}}>{month}</span>
                        <span style={{fontSize:11,color:'rgba(242,239,231,.5)'}}>
                          {staff.length} birthday{staff.length!==1?'s':''}
                        </span>
                      </div>
                      {staff.map(s => {
                        const isToday = new Date(Date.now()+3600000).getUTCMonth()===parseInt(s.birth_month)-1 &&
                          new Date(Date.now()+3600000).getUTCDate()===parseInt(s.birth_day);
                        return (
                          <div key={s.id} style={{display:'flex',alignItems:'center',gap:10,
                            padding:'10px 14px',borderBottom:'1px solid #F2EFE7',
                            background:isToday?'#FFF8E7':'#fff'}}>
                            <div style={{width:34,height:34,borderRadius:'50%',flexShrink:0,
                              background:'#556B2F',display:'flex',alignItems:'center',
                              justifyContent:'center',overflow:'hidden'}}>
                              {s.photo_url
                                ? <img src={s.photo_url} alt={s.name} style={{width:'100%',height:'100%',objectFit:'cover'}} />
                                : <span style={{fontSize:14,fontWeight:700,color:'#F2EFE7'}}>{s.name?.charAt(0)}</span>
                              }
                            </div>
                            <div style={{flex:1}}>
                              <div style={{fontSize:12,fontWeight:500,color:'#3B2A21'}}>
                                {s.name} {isToday && '🎂'}
                              </div>
                              <div style={{fontSize:10,color:'#8A8A8A'}}>{s.birthday_display}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Bonus Settings - Doris only */}
          {activeTab==='bonus' && isSuperAdmin && (
            <div style={{maxWidth:500}}>
              <div style={{background:'#fff',border:'1px solid var(--border-light)',
                borderRadius:10,padding:24}}>
                <h3 style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.2rem',
                  color:'#3B2A21',marginBottom:6}}>Birthday Bonus by Role</h3>
                <p style={{fontSize:12,color:'#8A8A8A',marginBottom:20}}>
                  Set the birthday bonus amount per role. Set 0 to disable bonus for that role.
                  Bonuses require your approval before being added to payroll.
                </p>
                {bonusSettings.map((s,i) => (
                  <div key={s.role} className="form-group">
                    <label className="form-label">
                      {ROLE_LABELS[s.role]||s.role} (₦)
                    </label>
                    <input type="number" className="form-control"
                      value={s.bonus_amount||0}
                      onChange={e => {
                        const updated = [...bonusSettings];
                        updated[i] = {...s, bonus_amount: e.target.value};
                        setBonusSettings(updated);
                      }} />
                  </div>
                ))}
                <button className="btn btn-primary" onClick={saveBonus} disabled={savingBonus}>
                  {savingBonus?'Saving...':'Save Bonus Settings'}
                </button>
              </div>
            </div>
          )}

          {/* Bonus Requests - Doris only */}
          {activeTab==='requests' && isSuperAdmin && (
            <div>
              {bonusRequests.length === 0 ? (
                <div className="empty-state"><h3>No bonus requests yet</h3></div>
              ) : (
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                  <thead><tr style={{background:'#556B2F'}}>
                    {['Staff','Role','Year','Bonus Amount','Status',''].map(h=>(
                      <th key={h} style={{padding:'9px 12px',textAlign:'left',fontSize:9,
                        color:'#F2EFE7',textTransform:'uppercase',letterSpacing:'.06em'}}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {bonusRequests.map((r,i)=>(
                      <tr key={r.id} style={{background:i%2===0?'#fff':'#F9F9F9'}}>
                        <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>{r.staff_name}</td>
                        <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>{ROLE_LABELS[r.staff_role]||r.staff_role}</td>
                        <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8'}}>{r.year}</td>
                        <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',
                          fontFamily:'Cormorant Garamond,serif',fontSize:14,fontWeight:600,color:'#556B2F'}}>
                          ₦{Number(r.amount).toLocaleString()}
                        </td>
                        <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8'}}>
                          <span style={{fontSize:9,fontWeight:700,padding:'2px 8px',borderRadius:20,
                            textTransform:'uppercase',
                            background:r.status==='approved'?'#E8F5EB':r.status==='rejected'?'#FDEAEA':'#FFF3DC',
                            color:r.status==='approved'?'#2E6B3A':r.status==='rejected'?'#8F2E24':'#8B5E0A'}}>
                            {r.status}
                          </span>
                        </td>
                        <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8'}}>
                          {r.status==='pending' && (
                            <div style={{display:'flex',gap:4}}>
                              <button className="btn btn-sm" style={{background:'#2E6B3A',color:'#fff',
                                border:'none',fontSize:10}} onClick={()=>approveBonus(r.id)}>✓ Approve</button>
                              <button className="btn btn-danger btn-sm" style={{fontSize:10}}
                                onClick={()=>{setRejectModal(r.id);setRejectReason('');}}>✗</button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}

      {/* Reject modal */}
      {rejectModal && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:400}}>
            <div className="modal-hdr">
              <span className="modal-title">Reject Birthday Bonus</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setRejectModal(null)}>X</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Reason</label>
                <textarea className="form-control" rows={3} value={rejectReason}
                  onChange={e=>setRejectReason(e.target.value)} />
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setRejectModal(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={rejectBonus}>Reject</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
